import Vue, { computed } from 'vue'

import { useTheme } from '@/shared/lib/model'
import { GetMainDataResponse, Status } from '@/shared/api/rest/types'

// import { useTheme } from '@/shared/lib/model'

type Merchant = {
    slugId: string
    name: string
    amount: Maybe<string>
    logoUrl: string
    list: {
        name: string
        value: string
    }[]
    link: Maybe<string>
}

type AppLoadingState = {
    kind: 'loading'
    merchant: null
}

type AppInvalidLinkState = {
    kind: 'invalid-state'
    merchant: Merchant
}

type AppIdleState = {
    kind: 'idle'
    merchant: Merchant
}

type AppResolvedState = {
    kind: 'resolved'
    merchant: Merchant
}

type AppRejectedState = {
    kind: 'rejected'
    merchant: Merchant
}

type AppApiFailState = {
    kind: 'api-fail'
    merchant: null
}

type AppExpiredState = {
    kind: 'expired'
    merchant: null
}

type AppState = {
    urlType: 'static' | 'dynamic'
    url: string
    timestamp: Maybe<number>
    removeHeader: boolean
    autoRedirect: boolean
    status: Maybe<Status>
    serverTime: Maybe<number>
} & (
    | AppLoadingState
    | AppInvalidLinkState
    | AppIdleState
    | AppResolvedState
    | AppRejectedState
    | AppApiFailState
    | AppExpiredState
)

export type Kind = AppState['kind']

const state = Vue.observable<AppState>({
    urlType: 'static',
    kind: 'loading',
    merchant: null,
    url: '',
    timestamp: null,
    removeHeader: false,
    autoRedirect: false,
    status: null,
    serverTime: null,
})

const analyticsData = Vue.observable<{ value: Maybe<GetMainDataResponse> }>({
    value: null,
})

export const useAppStore = () => {
    const setupUrlType = (urlType: AppState['urlType']) => {
        state.urlType = urlType
    }

    const setupAnalyticsData = (data: Maybe<GetMainDataResponse>) => {
        analyticsData.value = data
    }

    const setupUrl = (url: AppState['url']) => {
        state.url = url
    }

    const setupTimestamp = (url: AppState['timestamp']) => {
        state.timestamp = url
    }

    const setupAutoRedirect = (autoRedirect: AppState['autoRedirect']) => {
        state.autoRedirect = autoRedirect
    }

    const setupRemoveHeader = (removeHeader: AppState['removeHeader']) => {
        state.removeHeader = removeHeader
    }

    const setupStatus = (status: AppState['status']) => {
        state.status = status
    }

    const setupServerTime = (serverTime: AppState['serverTime']) => {
        state.serverTime = serverTime
    }

    const appStateReceived = (
        data:
            | AppInvalidLinkState
            | AppIdleState
            | AppResolvedState
            | AppRejectedState
            | AppApiFailState
    ) => {
        Object.assign(state, data)
    }

    const appStateExpired = () => {
        Object.assign(state, {
            ...state,
            kind: 'expired',
        })
    }

    const finished = (data: AppResolvedState | AppRejectedState) => {
        Object.assign(state, data)
    }

    const { theme } = useTheme()

    const logoUrl = computed(() => {
        const isStaticLogo = state.merchant?.logoUrl?.indexOf('.svg') === -1
        return isStaticLogo
            ? state.merchant?.logoUrl
            : `${state.merchant?.logoUrl}/${theme.value}/logo.svg`
    })

    return {
        setupUrlType,
        appStateReceived,
        finished,
        state,
        setupUrl,
        logoUrl,
        setupTimestamp,
        appStateExpired,
        setupAutoRedirect,
        setupRemoveHeader,
        setupAnalyticsData,
        analyticsData,
        setupStatus,
        setupServerTime,
    }
}
